import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Divider, MenuItem, Stack, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme } from '@mui/material';
import ChatPanel from './ChatPanel';
import MessageInput from './MessageInput';
import { useConfigStore } from '@/stores/useConfigStore';
import { useChatStore } from '@/stores/useChatStore';
import { useSessionStore } from '@/stores/useSessionStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { useStreamingChat } from '@/hooks/useStreamingChat';
import { setPracticeSelection } from '@/api/sessions';
import { hasPermission } from '@/utils/permissions';
import type { ModelLabel, PracticeMode } from '@/types/session';
import { confirmDiscardDrafts, useGuardedNavigate } from '@/hooks/useDraftGuard';

const DualChatView: React.FC = () => {
  const navigate = useGuardedNavigate();
  const session = useSessionStore(s => s.currentSession);
  const sessionId = session?.session_id || null;
  const { startStream } = useStreamingChat(sessionId);
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const [mobileSide, setMobileSide] = useState<ModelLabel>('left');
  const [separate, setSeparate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const left = useConfigStore(s => s.leftConfig);
  const right = useConfigStore(s => s.rightConfig);
  const modelName = (side: ModelLabel) => session?.practice_models?.[side]?.name || (side === 'left' ? left.name : right.name);
  const leftMessages = useChatStore(s => s.leftMessages);
  const rightMessages = useChatStore(s => s.rightMessages);
  const streaming = useChatStore(s => s.isStreamingLeft || s.isStreamingRight);
  const streamError = useChatStore(s => s.error);
  const permissions = useAuthStore(s => s.permissions);
  const actions = useAuthStore(s => s.actions);
  const user = useAuthStore(s => s.user);
  const token = useAuthStore(s => s.token);
  const permissionVersion = useAuthStore(s => s.permissionVersion);
  const generation = useRef(0);
  const started = leftMessages.length > 0 || rightMessages.length > 0 || streaming || (session?.turn_count ?? 0) > 0;
  const mode = session?.practice_mode || 'legacy';
  const single = mode === 'single';
  const selected = session?.selected_model || 'left';
  const writable = hasPermission(permissions, 'practice', 'write') && hasPermission(permissions, 'conversations', 'write') &&
    (session?.user_id === user?.user_id || Boolean(actions['conversations.read_all'] && actions['conversations.edit_all']));
  useEffect(() => {
    generation.current++; setSaving(false); setError(''); setSeparate(mode === 'legacy');
    setMobileSide(selected);
    return () => { generation.current++; };
  }, [sessionId, token, permissionVersion]);
  const change = async (nextMode: PracticeMode, side: ModelLabel | null) => {
    if (!sessionId || started || saving || !writable) return;
    if (!confirmDiscardDrafts('切换练习模式或模型会清空尚未发送的输入，是否继续？')) return;
    const stamp = generation.current;
    setSaving(true); setError('');
    try {
      const updated = await setPracticeSelection(sessionId, { practice_mode: nextMode, selected_model: side });
      if (stamp === generation.current) useSessionStore.getState().setCurrentSession(updated);
    } catch (err) { if (stamp === generation.current) setError(err instanceof Error ? err.message : '切换失败'); }
    finally { if (stamp === generation.current) setSaving(false); }
  };
  const sides: ModelLabel[] = single ? [selected] : ['left', 'right'];
  return <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ px: 2, py: 1.5, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
      <ToggleButtonGroup exclusive size="small" aria-label="练习模式" value={single ? 'single' : 'compare'} onChange={(_, value) => value && void change(value, value === 'single' ? selected : null)}>
        <ToggleButton value="single" disabled={!writable || started || saving}>大模型练习</ToggleButton>
        <ToggleButton value="compare" disabled={!writable || started || saving || !actions['practice.compare']}>双模型练习</ToggleButton>
      </ToggleButtonGroup>
      {single ? <TextField select size="small" label="练习模型" value={selected} disabled={!writable || started || saving} sx={{ minWidth: 160, flex: { xs: 1, sm: '0 1 240px' } }} onChange={e => void change('single', e.target.value as ModelLabel)}>
        <MenuItem value="left">A · {modelName('left')}</MenuItem><MenuItem value="right">B · {modelName('right')}</MenuItem>
      </TextField> : <ToggleButtonGroup exclusive size="small" aria-label="输入方式" value={separate ? 'separate' : 'shared'} onChange={(_, v) => { if (v && confirmDiscardDrafts('切换输入方式会清空尚未发送的内容，是否继续？')) setSeparate(v === 'separate'); }}>
        <ToggleButton value="shared" disabled={streaming || !actions['practice.compare']}>共同输入</ToggleButton><ToggleButton value="separate" disabled={streaming}>分别输入</ToggleButton>
      </ToggleButtonGroup>}
      {started && hasPermission(permissions, 'conversations') && <Button onClick={() => navigate('/sessions')} sx={{ whiteSpace: 'nowrap' }}>会话记录</Button>}
    </Stack>
    {(error || streamError) && <Alert severity="error" onClose={() => { setError(''); useChatStore.getState().setError(null); }}>{error || streamError}</Alert>}
    {!single && isMobile && <Tabs value={mobileSide} onChange={(_, side) => setMobileSide(side)} variant="fullWidth" aria-label="模型分支">
      {sides.map(side => <Tab key={side} value={side} title={modelName(side)} label={<Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{side === 'left' ? 'A' : 'B'} · {modelName(side)}</Box>} sx={{ minWidth: 0, maxWidth: '50%', height: 48 }} />)}
    </Tabs>}
    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {sides.map((side, index) => <React.Fragment key={side}>
        {index > 0 && !isMobile && <Divider orientation="vertical" flexItem />}
        <Box sx={{ flex: 1, minWidth: 0, display: !single && isMobile && mobileSide !== side ? 'none' : 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Typography variant="subtitle2" sx={{ px: 2, py: 1.5, fontWeight: 600, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', overflowWrap: 'anywhere' }}>{side === 'left' ? '模型 A' : '模型 B'} · {modelName(side)}</Typography>
          <Box sx={{ flex: 1, overflow: 'hidden' }}><ChatPanel modelLabel={side} /></Box>
          {!single && separate && <MessageInput key={`${sessionId}-${side}`} target={side} compact disabled={saving} startStream={startStream} />}
        </Box>
      </React.Fragment>)}
    </Box>
    {(single || !separate) && <MessageInput key={`${sessionId}-${single ? selected : 'shared'}`} target={single ? selected : 'both'} disabled={saving} startStream={startStream} />}
  </Box>;
};
export default DualChatView;
